/**
 * Human-readable descriptions for MapLibre style properties.
 * Used in field tooltips throughout the editor.
 */
export const PROPERTY_DESCRIPTIONS: Record<string, string> = {
  // ── Common ──
  "source": "The data source this layer visualizes. Must match a source ID defined in Sources.",
  "source-layer": "Which layer within a vector tile source to use. Vector sources contain multiple named layers.",
  "minzoom": "The minimum zoom level at which this layer is visible. The layer won't render below this zoom.",
  "maxzoom": "The maximum zoom level at which this layer is visible. The layer won't render at or above this zoom.",
  "filter": "An expression that filters which features from the source are rendered by this layer.",

  // ── Background ──
  "background-color": "The color of the map background. Only visible where no other layers cover it.",
  "background-pattern": "A sprite image to tile as a repeating pattern across the background.",
  "background-opacity": "How transparent the background is. 0 = fully transparent, 1 = fully opaque.",

  // ── Fill ──
  "fill-color": "The fill color of polygons. Supports data-driven expressions for per-feature coloring.",
  "fill-opacity": "Transparency of the polygon fill. 0 = invisible, 1 = solid.",
  "fill-outline-color": "Color of the polygon outline. Requires fill-antialias to be on.",
  "fill-antialias": "Smooths polygon edges. Disable for sharper edges but may show rendering artifacts.",
  "fill-pattern": "A sprite image to use as a repeating fill pattern instead of a solid color.",
  "fill-translate": "Shifts the polygon rendering by this many pixels from its actual position.",
  "fill-sort-key": "Controls draw order among features in the same layer. Lower values draw first.",

  // ── Line ──
  "line-color": "The color of the line stroke.",
  "line-width": "The thickness of the line in pixels. Supports zoom-based expressions for responsive widths.",
  "line-opacity": "Transparency of the line. 0 = invisible, 1 = solid.",
  "line-blur": "Amount of blur applied to the line. Higher values create a softer, glowing effect.",
  "line-cap": "Shape at the ends of lines. Butt = flat cut, Round = semicircle, Square = extended flat.",
  "line-join": "Shape at line corners. Miter = sharp, Round = smooth, Bevel = flattened.",
  "line-miter-limit": "Automatically converts sharp miter joins to bevel when the angle is too acute.",
  "line-dasharray": "Pattern of alternating dash and gap lengths. Example: [4, 2] = 4px dash, 2px gap.",
  "line-pattern": "A sprite image to stretch along the line instead of a solid color.",
  "line-gradient": "A color gradient along the line's length. Requires a GeoJSON source with lineMetrics enabled.",
  "line-offset": "Shifts the line perpendicular to its direction. Useful for road casings or parallel lines.",
  "line-gap-width": "Creates a gap in the center of the line, drawing two parallel strokes. Used for road casings.",
  "line-translate": "Shifts the line rendering by this many pixels from its actual position.",

  // ── Symbol: Text ──
  "text-field": "The text to display. Use [\"get\", \"name\"] to read from feature properties.",
  "text-font": "Font stack to use. Falls back through the list. Fonts must be available in the glyphs source.",
  "text-size": "Font size in pixels. Supports zoom-based expressions for responsive sizing.",
  "text-max-width": "Maximum line width in ems before text wraps. Prevents overly wide labels.",
  "text-line-height": "Spacing between lines of wrapped text, in ems.",
  "text-letter-spacing": "Space between characters in ems. Positive = spread out, negative = tighter.",
  "text-transform": "Automatically transform text case. None preserves original case.",
  "text-justify": "How multi-line text is aligned. Left, center, or right.",
  "text-anchor": "Which part of the text is placed at the anchor point. Affects positioning direction.",
  "text-offset": "Shifts the text from its anchor point in ems. [x, y] where positive x = right, positive y = down.",
  "text-radial-offset": "Shifts text away from its anchor in all directions. Works with variable-anchor placement.",
  "text-rotate": "Rotates the text clockwise by this many degrees.",
  "text-padding": "Extra space around the text used for collision detection. Prevents labels from overlapping.",
  "text-color": "The color of the text characters.",
  "text-opacity": "Transparency of the text. 0 = invisible, 1 = solid.",
  "text-halo-color": "Color of the outline/glow around text. Usually white or dark for contrast.",
  "text-halo-width": "Width of the text halo in pixels. 0 = no halo. Essential for readability over busy maps.",
  "text-halo-blur": "Softness of the halo edge. 0 = sharp outline, higher = softer glow.",
  "text-allow-overlap": "If true, text renders even when it overlaps other labels. Can cause visual clutter.",

  // ── Symbol: Icon ──
  "icon-image": "Name of the sprite image to use as an icon. Must exist in the sprite sheet.",
  "icon-size": "Scale factor for the icon. 1 = original size, 2 = double size.",
  "icon-rotate": "Rotates the icon clockwise by this many degrees.",
  "icon-anchor": "Which part of the icon is placed at the anchor point.",
  "icon-offset": "Shifts the icon from its anchor point in pixels.",
  "icon-padding": "Extra space around the icon for collision detection.",
  "icon-color": "Tint color applied to SDF (signed distance field) icons. Only works with SDF sprites.",
  "icon-opacity": "Transparency of the icon.",
  "icon-halo-color": "Halo color around SDF icons.",
  "icon-halo-width": "Width of the icon halo in pixels.",
  "icon-allow-overlap": "If true, icon renders even when overlapping other symbols.",

  // ── Symbol: Placement ──
  "symbol-placement": "Point = at geometry coordinates. Line = along lines. Line-center = centered on lines.",
  "symbol-spacing": "Minimum distance between symbols along a line in pixels.",
  "symbol-sort-key": "Controls which symbols draw on top when they overlap. Lower values = on top.",
  "symbol-z-order": "How overlapping symbols are ordered. Auto sorts by y-position for pseudo-3D.",
  "symbol-avoid-edges": "Prevents symbols from being cut off at tile boundaries.",

  // ── Circle ──
  "circle-radius": "Size of the circle in pixels. Supports data-driven expressions for bubble charts.",
  "circle-color": "Fill color of the circle.",
  "circle-opacity": "Transparency of the circle fill.",
  "circle-blur": "Amount of blur. 0 = sharp edge, 1 = fully blurred to center point only.",
  "circle-stroke-width": "Width of the circle's outline in pixels. 0 = no outline.",
  "circle-stroke-color": "Color of the circle's outline.",
  "circle-pitch-scale": "Map = circles scale with the map when pitched. Viewport = fixed screen size.",
  "circle-pitch-alignment": "Map = circles flat on the map surface. Viewport = always facing the camera.",
  "circle-translate": "Shifts circles from their actual position by this many pixels.",

  // ── Heatmap ──
  "heatmap-radius": "Influence radius of each point in pixels. Larger = smoother, less detail.",
  "heatmap-weight": "How much each point contributes to the density. Use a property for weighted heatmaps.",
  "heatmap-intensity": "Global intensity multiplier. Increase when zooming in to maintain visual density.",
  "heatmap-color": "Color ramp mapping density values (0–1) to colors. Uses interpolation expressions.",
  "heatmap-opacity": "Overall transparency of the heatmap layer.",

  // ── Fill Extrusion ──
  "fill-extrusion-color": "Color of the 3D extruded shape's surfaces.",
  "fill-extrusion-opacity": "Transparency of all extruded shapes in this layer (not per-feature).",
  "fill-extrusion-height": "Height of the extrusion in meters. Use a property like building height for realistic 3D.",
  "fill-extrusion-base": "Starting height of the extrusion in meters. Creates floating shapes when > 0.",
  "fill-extrusion-vertical-gradient": "Adds shading variation on the vertical walls for a more realistic look.",
  "fill-extrusion-pattern": "A sprite image to tile on the extruded surfaces.",

  // ── Raster ──
  "raster-opacity": "Transparency of the raster imagery.",
  "raster-hue-rotate": "Rotates all colors by this many degrees on the color wheel.",
  "raster-brightness-min": "Minimum brightness. Increase to lighten dark areas.",
  "raster-brightness-max": "Maximum brightness. Decrease to darken bright areas.",
  "raster-saturation": "Color saturation. -1 = grayscale, 0 = normal, 1 = vivid.",
  "raster-contrast": "Contrast adjustment. -1 = flat, 0 = normal, 1 = high contrast.",
  "raster-resampling": "Linear = smooth when zoomed in. Nearest = pixelated (good for data tiles).",
  "raster-fade-duration": "How long in milliseconds new tiles take to fade in when loading.",

  // ── Hillshade ──
  "hillshade-illumination-direction": "Direction the light comes from in degrees. 0 = top, 90 = right.",
  "hillshade-illumination-altitude": "Angle of the light above the horizon. 0 = flat, 90 = directly overhead.",
  "hillshade-exaggeration": "How pronounced the terrain relief appears. 0 = flat, 1 = maximum.",
  "hillshade-shadow-color": "Color of shadowed areas.",
  "hillshade-highlight-color": "Color of sunlit areas.",
  "hillshade-accent-color": "Color used to accent steep terrain.",
};

/** One-line descriptions for each layer type */
export const LAYER_TYPE_DESCRIPTIONS: Record<string, string> = {
  background: "Solid color or pattern that fills the entire map canvas behind all other layers.",
  fill: "Renders polygon geometries with a solid color, pattern, or data-driven styling.",
  line: "Renders line geometries as strokes with configurable width, color, dashes, and patterns.",
  symbol: "Renders point and line features as text labels and/or icons from the sprite sheet.",
  circle: "Renders point features as circles with configurable radius, color, and stroke.",
  heatmap: "Visualizes point density as a color gradient. Great for showing data concentration.",
  "fill-extrusion": "Renders polygons as 3D extruded shapes. Used for buildings and terrain features.",
  raster: "Displays raster tile imagery like satellite photos or terrain maps.",
  hillshade: "Renders terrain relief shading from elevation data (DEM source).",
};
